import {
  Archive,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  CircleHelp,
  FilePen,
  FileSignature,
  FileText,
  Flag,
  Hourglass,
  PackageCheck,
  Undo2,
} from 'lucide-react';
import { type HTMLAttributes } from 'react';

import { Badge, type BadgeProps } from './Badge';

export type CaseStatus =
  | 'rascunho'
  | 'em_analise'
  | 'encerrado_triagem'
  | 'encerrado_com_relatorio'
  | 'indefinido'
  | 'laudo_em_elaboracao'
  | 'eletronica_concluida'
  | 'aguardando_arquivos'
  | 'aguardando_revisao'
  | 'aguardando_revisor'
  | 'devolvido_pelo_revisor'
  | 'concluido'
  | 'finalizado'
  | 'arquivado'
  | 'assinado';

interface StatusMeta {
  variant: NonNullable<BadgeProps['variant']>;
  icon: typeof CircleDashed;
}

const STATUS_META: Record<CaseStatus, StatusMeta> = {
  rascunho: { variant: 'neutral', icon: CircleDashed },
  em_analise: { variant: 'info', icon: CircleDot },
  // E45.F2 — caso encerrado na triagem (raia isolada, preliminar).
  encerrado_triagem: { variant: 'neutral', icon: Flag },
  // E72.F2 — encerrado na triagem COM o Relatório Preliminar emitido. Ícone de
  // documento porque é o que separa este desfecho do anterior: aqui há PDF.
  encerrado_com_relatorio: { variant: 'neutral', icon: FileText },
  // E72.F2.T00 — destino de estado que este bundle não conhece (ver `statusMetaFor`).
  indefinido: { variant: 'neutral', icon: CircleHelp },
  // E23.F24 — escada de status do laudo.
  laudo_em_elaboracao: { variant: 'info', icon: FilePen },
  // E78.F1 — a raia eletrônica termina com documento disponível: verde e ícone
  // de pacote pronto, como o `finalizado`, e não o lápis de "em elaboração".
  eletronica_concluida: { variant: 'success', icon: PackageCheck },
  // E193.F1 (GUS-259) — caso digital parado até o cliente enviar o que falta.
  aguardando_arquivos: { variant: 'warning', icon: Hourglass },
  aguardando_revisao: { variant: 'warning', icon: Hourglass },
  aguardando_revisor: { variant: 'warning', icon: Hourglass },
  devolvido_pelo_revisor: { variant: 'danger', icon: Undo2 },
  concluido: { variant: 'success', icon: CheckCircle2 },
  finalizado: { variant: 'success', icon: PackageCheck },
  arquivado: { variant: 'neutral', icon: Archive },
  assinado: { variant: 'success', icon: FileSignature },
};

/**
 * Metadado do status, tolerante a valor que este bundle não conhece (E72.F2.T00).
 *
 * `STATUS_META[status]` devolvia `undefined` para um estado que o backend passou
 * a emitir antes de o navegador recarregar o bundle novo, e `meta.icon` estourava
 * a LISTA INTEIRA de casos — uma linha desconhecida derrubava a tela toda. O
 * recuo é neutro e nunca mostra o código cru: quem escolhe o rótulo é o i18n
 * (`uiStatusLabel`), que tem o mesmo recuo.
 */
export function statusMetaFor(status: string): StatusMeta {
  const meta = Object.prototype.hasOwnProperty.call(STATUS_META, status)
    ? STATUS_META[status as CaseStatus]
    : STATUS_META.indefinido;
  return meta;
}

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  status: CaseStatus;
  /** Label traduzido (i18n) — primitives não acessam i18n diretamente. */
  label: string;
}

export function StatusPill({ status, label, className, ...props }: StatusPillProps) {
  const meta = statusMetaFor(status);
  const Icon = meta.icon;
  return (
    <Badge
      variant={meta.variant}
      icon={<Icon className="h-3 w-3" aria-hidden />}
      data-status={status}
      className={className}
      {...props}
    >
      {label}
    </Badge>
  );
}
